import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getColumnConf, OVERTIME_COLUMN_KEYS } from 'utils/common';

const DETAIL_COLUMN_STORAGE_VERSION = 2;
const DETAIL_COLUMN_STORAGE_PREFIX = 'cinebudget-detail-column-layout';
const DETAIL_COLUMN_MIN_WIDTH = 48;
const DETAIL_COLUMN_MAX_WIDTH = 720;
const REQUIRED_VISIBLE_COLUMN_KEYS = new Set(['item']);

const COLUMN_MANAGER_LABELS = {
  account_number: 'Account #',
  currency: 'Currency',
  item: 'Item',
  tags: 'Tags',
  fringes: 'F%',
  fringe_total: 'Fringe Total',
  column_total: 'Total',
  actual: 'Actual',
  estimate_to_complete_override: 'Estimate to Complete',
  comment: 'Notes',
  ot1_quantity: 'OT1',
  ot2_quantity: 'OT2',
  ot3_quantity: 'OT3',
  ot4_quantity: 'OT4',
};

const FRINGE_TOTAL_COLUMN = {
  key: 'fringe_total',
  label: 'Fringe Total',
  type: 'showValue',
  width: '100px',
  dataType: 'decimal',
  required: false,
};

const withFringeTotalColumn = columns => {
  if (columns.some(column => column.key === FRINGE_TOTAL_COLUMN.key)) {
    return columns;
  }

  const nextColumns = [...columns];
  const totalIndex = nextColumns.findIndex(col => col.key === 'column_total');

  if (totalIndex !== -1) {
    nextColumns.splice(totalIndex, 0, FRINGE_TOTAL_COLUMN);
  } else {
    nextColumns.push(FRINGE_TOTAL_COLUMN);
  }

  return nextColumns;
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const getColumnGroupKey = column => column.columnGroupKey || column.key;
const getColumnVisibilityKey = column =>
  column.visibilityKey || getColumnGroupKey(column);

const getLogicalColumns = columns => {
  const seenGroupKeys = new Set();

  return columns.filter(column => {
    const groupKey = getColumnGroupKey(column);
    if (seenGroupKeys.has(groupKey)) return false;
    seenGroupKeys.add(groupKey);
    return true;
  });
};

const getStorageKey = budgetId =>
  budgetId ? `${DETAIL_COLUMN_STORAGE_PREFIX}:${budgetId}` : null;

const readStoredPreferences = budgetId => {
  const storageKey = getStorageKey(budgetId);
  if (typeof window === 'undefined' || !storageKey) return null;

  try {
    const stored = JSON.parse(
      window.localStorage?.getItem(storageKey) || 'null'
    );
    return stored?.version === DETAIL_COLUMN_STORAGE_VERSION ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredPreferences = (budgetId, preferences) => {
  const storageKey = getStorageKey(budgetId);
  if (typeof window === 'undefined' || !storageKey) return;

  try {
    window.localStorage?.setItem(
      storageKey,
      JSON.stringify({
        version: DETAIL_COLUMN_STORAGE_VERSION,
        order: preferences.order,
        hidden: preferences.hidden,
        widths: preferences.widths,
      })
    );
  } catch {
    // Ignore storage failures; the in-memory preference still applies.
  }
};

const normalizeColumnOrder = (order, defaultOrder) => {
  const defaultIndexes = defaultOrder.reduce(
    (acc, key, index) => ({ ...acc, [key]: index }),
    {}
  );
  const result = (Array.isArray(order) ? order : []).filter(
    (key, index, source) =>
      hasOwn(defaultIndexes, key) && source.indexOf(key) === index
  );

  defaultOrder.forEach(key => {
    if (result.includes(key)) return;

    const insertAt = result.findIndex(
      existingKey => defaultIndexes[existingKey] > defaultIndexes[key]
    );
    if (insertAt === -1) {
      result.push(key);
    } else {
      result.splice(insertAt, 0, key);
    }
  });

  return result;
};

const normalizeHiddenColumns = (hiddenColumns, columns) =>
  columns.reduce((acc, column) => {
    const visibilityKey = getColumnVisibilityKey(column);
    acc[column.key] = REQUIRED_VISIBLE_COLUMN_KEYS.has(visibilityKey)
      ? false
      : Boolean(hiddenColumns?.[visibilityKey]);
    return acc;
  }, {});

const normalizeColumnWidthValue = value => {
  const parsed = Number.parseFloat(String(value || '').replace('px', ''));

  if (!Number.isFinite(parsed)) return null;

  return Math.min(
    Math.max(Math.round(parsed), DETAIL_COLUMN_MIN_WIDTH),
    DETAIL_COLUMN_MAX_WIDTH
  );
};

const normalizeColumnWidths = (widths, columns) => {
  const columnKeys = new Set(columns.map(column => column.key));

  return Object.entries(widths || {}).reduce((acc, [key, value]) => {
    const normalizedWidth = normalizeColumnWidthValue(value);

    if (columnKeys.has(key) && normalizedWidth) {
      acc[key] = normalizedWidth;
    }

    return acc;
  }, {});
};

const applyColumnWidths = (columns, widths) =>
  columns.map(column => {
    const width = normalizeColumnWidthValue(widths?.[column.key]);

    return width
      ? {
          ...column,
          width: `${width}px`,
        }
      : column;
  });

const getDefaultHiddenColumns = showAccountNumbers => ({
  account_number: !showAccountNumbers,
  currency: true,
  tags: true,
  actual: true,
  ...OVERTIME_COLUMN_KEYS.reduce(
    (hidden, columnKey) => ({ ...hidden, [columnKey]: true }),
    {}
  ),
});

const getResetHiddenColumns = columns =>
  columns.reduce((acc, column) => {
    const visibilityKey = getColumnVisibilityKey(column);
    acc[visibilityKey] = OVERTIME_COLUMN_KEYS.includes(visibilityKey);
    return acc;
  }, {});

const preferencesAreEqual = (left, right) =>
  left?.budgetId === right?.budgetId &&
  JSON.stringify(left?.order || []) === JSON.stringify(right?.order || []) &&
  JSON.stringify(left?.hidden || {}) === JSON.stringify(right?.hidden || {}) &&
  JSON.stringify(left?.widths || {}) === JSON.stringify(right?.widths || {});

const applyColumnOrder = (columns, order) => {
  const defaultOrder = getLogicalColumns(columns).map(getColumnGroupKey);
  const normalizedOrder = normalizeColumnOrder(order, defaultOrder);
  const columnsByGroupKey = columns.reduce((groups, column) => {
    const groupKey = getColumnGroupKey(column);
    const groupColumns = groups.get(groupKey) || [];
    groupColumns.push(column);
    groups.set(groupKey, groupColumns);
    return groups;
  }, new Map());

  return normalizedOrder.flatMap(key => columnsByGroupKey.get(key) || []);
};

const getColumnManagerLabel = column => {
  if (COLUMN_MANAGER_LABELS[column.key])
    return COLUMN_MANAGER_LABELS[column.key];
  return typeof column.label === 'string' ? column.label : column.key;
};

/**
 * Custom hook for managing column visibility and configuration
 */
export const useColumnManagement = ({
  selectedBudget,
  unitTypes,
  detailsRows,
  selectedItem,
  setNavWidth,
  fringeGroups, // Optional: linked fringe groups
  showAccountNumbers = false,
  setShowAccountNumbers,
}) => {
  const budgetId = selectedBudget?.budget;
  const [columnPreferences, setColumnPreferences] = useState({
    budgetId: null,
    order: [],
    hidden: {},
    widths: {},
  });
  const [focus, setFocus] = useState(false);
  const [compactColumn, setCompactColumn] = useState({ comment: false });
  const lastSyncedAccountVisibleRef = useRef(showAccountNumbers);

  // Check if any row has fringes (direct or via groups)
  const hasFringes = useMemo(() => {
    const rows = detailsRows[selectedItem?.id] || [];

    // Direct fringes on rows
    const rowHasFringes = rows.some(
      row => Array.isArray(row?.fringe) && row.fringe.length > 0
    );

    // Fringes applied via groups (fringeGroups from redux)
    const groupsHaveFringes =
      fringeGroups && Object.keys(fringeGroups || {}).length > 0;

    return rowHasFringes || groupsHaveFringes;
  }, [detailsRows, selectedItem, fringeGroups]);

  const setColumnVisibility = useCallback(
    (column, isVisible) => {
      if (REQUIRED_VISIBLE_COLUMN_KEYS.has(column)) return;

      setColumnPreferences(prevPreferences => ({
        ...prevPreferences,
        hidden: {
          ...prevPreferences.hidden,
          [column]: !isVisible,
        },
      }));

      if (column === 'account_number') {
        lastSyncedAccountVisibleRef.current = isVisible;
        setShowAccountNumbers?.(isVisible);
      }

      setNavWidth?.();
    },
    [setNavWidth, setShowAccountNumbers]
  );

  // Column toggle functions
  const toggleColumn = useCallback(
    column => () => {
      const isCurrentlyHidden = Boolean(columnPreferences.hidden?.[column]);
      setColumnVisibility(column, isCurrentlyHidden);
    },
    [columnPreferences.hidden, setColumnVisibility]
  );

  const toggleCompactColumn = useCallback(
    column => () => {
      setCompactColumn(prevCompactColumn => ({
        ...prevCompactColumn,
        [column]: !prevCompactColumn[column],
      }));
      setColumnPreferences(prevPreferences => {
        if (!hasOwn(prevPreferences.widths, column)) return prevPreferences;

        const nextWidths = { ...(prevPreferences.widths || {}) };
        delete nextWidths[column];

        return {
          ...prevPreferences,
          widths: nextWidths,
        };
      });
      setNavWidth?.();
    },
    [setNavWidth]
  );

  // Column configuration
  const baseColumns = useMemo(
    () =>
      getColumnConf(
        selectedBudget,
        unitTypes,
        toggleColumn,
        setFocus,
        focus,
        hasFringes,
        true,
        compactColumn,
        toggleCompactColumn
      ),
    [
      selectedBudget,
      unitTypes,
      toggleColumn,
      focus,
      hasFringes,
      compactColumn,
      toggleCompactColumn,
    ]
  );

  const managedColumns = useMemo(
    () => withFringeTotalColumn(baseColumns),
    [baseColumns]
  );

  const logicalManagedColumns = useMemo(
    () => getLogicalColumns(managedColumns),
    [managedColumns]
  );

  // The rendered grid only shows Fringe Total while the fringe group is expanded.
  const rawUpdatedColumns = useMemo(
    () => (focus ? managedColumns : baseColumns),
    [baseColumns, focus, managedColumns]
  );

  const managedColumnOrder = useMemo(
    () => logicalManagedColumns.map(getColumnGroupKey),
    [logicalManagedColumns]
  );
  const managedColumnOrderKey = managedColumnOrder.join('|');

  useEffect(() => {
    if (!budgetId || managedColumnOrder.length === 0) return;

    const storedPreferences = readStoredPreferences(budgetId);
    const defaultHiddenColumns = getDefaultHiddenColumns(showAccountNumbers);
    const storedHiddenColumns = storedPreferences?.hidden || {};
    const hasStoredAccountPreference = hasOwn(
      storedHiddenColumns,
      'account_number'
    );
    const nextHiddenColumns = {
      ...defaultHiddenColumns,
      ...storedHiddenColumns,
    };
    const nextPreferences = {
      budgetId,
      order: normalizeColumnOrder(storedPreferences?.order, managedColumnOrder),
      hidden: normalizeHiddenColumns(nextHiddenColumns, managedColumns),
      widths: normalizeColumnWidths(storedPreferences?.widths, managedColumns),
    };
    const accountVisible = !nextPreferences.hidden.account_number;

    if (hasStoredAccountPreference) {
      lastSyncedAccountVisibleRef.current = accountVisible;
      setShowAccountNumbers?.(accountVisible);
    } else {
      lastSyncedAccountVisibleRef.current = showAccountNumbers;
    }

    setColumnPreferences(prevPreferences =>
      preferencesAreEqual(prevPreferences, nextPreferences)
        ? prevPreferences
        : nextPreferences
    );
    // managedColumnOrderKey intentionally scopes reloads to structural columns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetId, managedColumnOrderKey]);

  useEffect(() => {
    if (
      !budgetId ||
      columnPreferences.budgetId !== budgetId ||
      lastSyncedAccountVisibleRef.current === showAccountNumbers
    ) {
      return;
    }

    lastSyncedAccountVisibleRef.current = showAccountNumbers;
    setColumnPreferences(prevPreferences => ({
      ...prevPreferences,
      hidden: {
        ...prevPreferences.hidden,
        account_number: !showAccountNumbers,
      },
    }));
  }, [budgetId, columnPreferences.budgetId, showAccountNumbers]);

  useEffect(() => {
    if (!budgetId || columnPreferences.budgetId !== budgetId) return;
    writeStoredPreferences(budgetId, columnPreferences);
  }, [budgetId, columnPreferences]);

  const columns = useMemo(
    () =>
      applyColumnOrder(
        applyColumnWidths(baseColumns, columnPreferences.widths),
        columnPreferences.order
      ),
    [baseColumns, columnPreferences.order, columnPreferences.widths]
  );

  const updatedColumns = useMemo(
    () =>
      applyColumnOrder(
        applyColumnWidths(rawUpdatedColumns, columnPreferences.widths),
        columnPreferences.order
      ),
    [columnPreferences.order, columnPreferences.widths, rawUpdatedColumns]
  );

  const hiddenColumn = useMemo(
    () =>
      normalizeHiddenColumns(
        {
          ...getDefaultHiddenColumns(showAccountNumbers),
          ...columnPreferences.hidden,
        },
        updatedColumns
      ),
    [columnPreferences.hidden, showAccountNumbers, updatedColumns]
  );

  const managedHiddenColumn = useMemo(
    () =>
      normalizeHiddenColumns(
        {
          ...getDefaultHiddenColumns(showAccountNumbers),
          ...columnPreferences.hidden,
        },
        managedColumns
      ),
    [columnPreferences.hidden, managedColumns, showAccountNumbers]
  );

  // Check if fringe total column exists
  const hasFringeTotalColumn = updatedColumns.some(
    col => col.key === 'fringe_total'
  );

  const effectiveManagedHiddenColumn = useMemo(
    () => ({
      ...managedHiddenColumn,
      fringe_total:
        Boolean(managedHiddenColumn.fringe_total) || !hasFringeTotalColumn,
    }),
    [hasFringeTotalColumn, managedHiddenColumn]
  );

  const columnManagerColumns = useMemo(
    () =>
      applyColumnOrder(logicalManagedColumns, columnPreferences.order).map(
        column => ({
          hidden: Boolean(effectiveManagedHiddenColumn[column.key]),
          key: getColumnGroupKey(column),
          label: getColumnManagerLabel(column),
          locked: REQUIRED_VISIBLE_COLUMN_KEYS.has(getColumnGroupKey(column)),
          overtimeLabel: column.overtimeLabel,
        })
      ),
    [
      columnPreferences.order,
      effectiveManagedHiddenColumn,
      logicalManagedColumns,
    ]
  );

  const hiddenColumnCount = useMemo(
    () =>
      columnManagerColumns.filter(column => column.hidden && !column.locked)
        .length,
    [columnManagerColumns]
  );

  const reorderColumns = useCallback(
    (sourceIndex, destinationIndex) => {
      if (sourceIndex === destinationIndex) return;

      setColumnPreferences(prevPreferences => {
        const currentOrder = normalizeColumnOrder(
          prevPreferences.order,
          managedColumnOrder
        );
        const nextOrder = [...currentOrder];
        const [movedColumn] = nextOrder.splice(sourceIndex, 1);

        if (!movedColumn) return prevPreferences;
        nextOrder.splice(destinationIndex, 0, movedColumn);

        return {
          ...prevPreferences,
          order: nextOrder,
        };
      });
      setNavWidth?.();
    },
    [managedColumnOrder, setNavWidth]
  );

  const reorderColumnsByKey = useCallback(
    (sourceKey, destinationKey, insertAfter = false) => {
      if (!sourceKey || !destinationKey || sourceKey === destinationKey) return;

      setColumnPreferences(prevPreferences => {
        const currentOrder = normalizeColumnOrder(
          prevPreferences.order,
          managedColumnOrder
        );
        const sourceIndex = currentOrder.indexOf(sourceKey);
        const destinationIndex = currentOrder.indexOf(destinationKey);

        if (sourceIndex === -1 || destinationIndex === -1) {
          return prevPreferences;
        }

        const nextOrder = currentOrder.filter(key => key !== sourceKey);
        const adjustedDestinationIndex = nextOrder.indexOf(destinationKey);

        if (adjustedDestinationIndex === -1) return prevPreferences;

        nextOrder.splice(
          adjustedDestinationIndex + (insertAfter ? 1 : 0),
          0,
          sourceKey
        );

        return {
          ...prevPreferences,
          order: nextOrder,
        };
      });
      setNavWidth?.();
    },
    [managedColumnOrder, setNavWidth]
  );

  const applyManagedColumnOrder = useCallback(
    columnKeys => {
      if (!Array.isArray(columnKeys) || columnKeys.length === 0) return;

      setColumnPreferences(prevPreferences => {
        const nextOrder = normalizeColumnOrder(columnKeys, managedColumnOrder);

        if (
          JSON.stringify(nextOrder) ===
          JSON.stringify(prevPreferences.order || [])
        ) {
          return prevPreferences;
        }

        return {
          ...prevPreferences,
          order: nextOrder,
        };
      });
      setNavWidth?.();
    },
    [managedColumnOrder, setNavWidth]
  );

  const resizeColumns = useCallback(
    widths => {
      setColumnPreferences(prevPreferences => {
        const nextWidths = normalizeColumnWidths(
          {
            ...prevPreferences.widths,
            ...widths,
          },
          managedColumns
        );

        if (
          JSON.stringify(nextWidths) ===
          JSON.stringify(prevPreferences.widths || {})
        ) {
          return prevPreferences;
        }

        return {
          ...prevPreferences,
          budgetId: prevPreferences.budgetId || budgetId,
          widths: nextWidths,
        };
      });
      setNavWidth?.();
    },
    [budgetId, managedColumns, setNavWidth]
  );

  const toggleManagedColumn = useCallback(
    column => {
      const shouldShow = Boolean(effectiveManagedHiddenColumn[column]);

      if (column === 'fringe_total' && shouldShow) {
        setFocus(true);
      }

      setColumnVisibility(column, shouldShow);
    },
    [effectiveManagedHiddenColumn, setColumnVisibility]
  );

  const resetColumnPreferences = useCallback(() => {
    const resetHiddenColumns = getResetHiddenColumns(managedColumns);

    setColumnPreferences(prevPreferences => ({
      ...prevPreferences,
      order: managedColumnOrder,
      hidden: normalizeHiddenColumns(resetHiddenColumns, managedColumns),
      widths: {},
    }));

    setFocus(true);
    lastSyncedAccountVisibleRef.current = true;
    setShowAccountNumbers?.(true);
    setNavWidth?.();
  }, [managedColumnOrder, managedColumns, setNavWidth, setShowAccountNumbers]);

  return {
    hiddenColumn,
    focus,
    setFocus,
    hasFringes,
    columns,
    updatedColumns,
    hasFringeTotalColumn,
    columnManagerColumns,
    hiddenColumnCount,
    toggleColumn,
    toggleManagedColumn,
    reorderColumns,
    reorderColumnsByKey,
    applyManagedColumnOrder,
    resizeColumns,
    resetColumnPreferences,
  };
};
